class LasercoTheme {
  constructor() {
    this.header = document.querySelector(".site-header");
    this.initNavigation();
    this.initSearch();
    this.initTabs();
    this.initSlideshows();
    this.initFaqs();
    this.initMegaMenuPreviews();
    this.initProductGallery();
    this.initWhatsAppOrdering();
    this.initQuantity();
    this.initStickyWhatsApp();
  }

  initNavigation() {
    const navShell = document.querySelector("[data-nav-shell]");
    const menuToggle = document.querySelector("[data-menu-toggle]");
    const menuClose = document.querySelector("[data-menu-close]");
    const items = document.querySelectorAll("[data-nav-item]");

    const closeAll = (except) => {
      items.forEach((item) => {
        if (item !== except) {
          item.classList.remove("is-open");
          item
            .querySelector("[aria-expanded]")
            ?.setAttribute("aria-expanded", "false");
        }
      });
    };

    items.forEach((item) => {
      const trigger = item.querySelector("[data-nav-trigger]");
      if (!trigger) return;
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        const shouldOpen = !item.classList.contains("is-open");
        closeAll(item);
        item.classList.toggle("is-open", shouldOpen);
        trigger.setAttribute("aria-expanded", String(shouldOpen));
      });
      item.addEventListener("mouseenter", () => {
        if (window.innerWidth < 990) return;
        closeAll(item);
        item.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");
      });
      item.addEventListener("mouseleave", () => {
        if (window.innerWidth < 990) return;
        item.classList.remove("is-open");
        trigger.setAttribute("aria-expanded", "false");
      });
    });

    const openMobile = () => {
      navShell?.classList.add("is-open");
      document.body.classList.add("menu-open");
      menuToggle?.setAttribute("aria-expanded", "true");
    };
    const closeMobile = () => {
      navShell?.classList.remove("is-open");
      document.body.classList.remove("menu-open");
      menuToggle?.setAttribute("aria-expanded", "false");
    };
    menuToggle?.addEventListener("click", openMobile);
    menuClose?.addEventListener("click", closeMobile);
    document.addEventListener("keyup", (event) => {
      if (event.key === "Escape") {
        closeMobile();
        closeAll();
        this.closeSearch();
      }
    });
  }

  initSearch() {
    this.searchOverlay = document.querySelector("[data-search-overlay]");
    document.querySelectorAll("[data-search-open]").forEach((button) => {
      button.addEventListener("click", () => {
        this.searchOverlay?.classList.add("is-open");
        document.body.classList.add("search-open");
        setTimeout(
          () => this.searchOverlay?.querySelector("input")?.focus(),
          100,
        );
      });
    });
    document
      .querySelectorAll("[data-search-close]")
      .forEach((button) =>
        button.addEventListener("click", () => this.closeSearch()),
      );
    this.searchOverlay?.addEventListener("click", (event) => {
      if (event.target === this.searchOverlay) this.closeSearch();
    });
  }

  closeSearch() {
    this.searchOverlay?.classList.remove("is-open");
    document.body.classList.remove("search-open");
  }

  initTabs() {
    document.querySelectorAll("[data-tabs]").forEach((tabs) => {
      const buttons = tabs.querySelectorAll("[data-tab]");
      const panels = tabs.querySelectorAll("[data-tab-panel]");
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          buttons.forEach((item) =>
            item.classList.toggle("is-active", item === button),
          );
          panels.forEach((panel) =>
            panel.classList.toggle(
              "is-active",
              panel.dataset.tabPanel === button.dataset.tab,
            ),
          );
        });
      });
    });
  }

  initSlideshows() {
    document.querySelectorAll("[data-slideshow]").forEach((slideshow) => {
      const slides = [...slideshow.querySelectorAll("[data-slide]")];
      const dots = [...slideshow.querySelectorAll("[data-slide-dot]")];
      if (slides.length < 2) return;

      let activeIndex = Math.max(
        0,
        slides.findIndex((slide) => slide.classList.contains("is-active")),
      );
      let timer;
      const autoplay =
        slideshow.dataset.autoplay === "true" &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const speed = Number(slideshow.dataset.speed || 6000);

      const showSlide = (nextIndex) => {
        activeIndex = (nextIndex + slides.length) % slides.length;
        slides.forEach((slide, index) => {
          const isActive = index === activeIndex;
          slide.classList.toggle("is-active", isActive);
          slide.setAttribute("aria-hidden", String(!isActive));
        });
        dots.forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.classList.toggle("is-active", isActive);
          dot.setAttribute("aria-current", String(isActive));
        });
      };

      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        if (autoplay)
          timer = window.setInterval(() => showSlide(activeIndex + 1), speed);
      };

      slideshow
        .querySelector("[data-slide-prev]")
        ?.addEventListener("click", () => {
          showSlide(activeIndex - 1);
          start();
        });
      slideshow
        .querySelector("[data-slide-next]")
        ?.addEventListener("click", () => {
          showSlide(activeIndex + 1);
          start();
        });
      dots.forEach((dot) =>
        dot.addEventListener("click", () => {
          showSlide(Number(dot.dataset.slideDot));
          start();
        }),
      );

      slideshow.addEventListener("mouseenter", stop);
      slideshow.addEventListener("mouseleave", start);
      slideshow.addEventListener("focusin", stop);
      slideshow.addEventListener("focusout", start);
      start();
    });
  }

  initFaqs() {
    document.querySelectorAll("[data-published-faq]").forEach((faq) => {
      const items = [...faq.querySelectorAll("[data-faq-item]")];

      const setItem = (item, open) => {
        const trigger = item.querySelector("[data-faq-trigger]");
        const panel = item.querySelector("[data-faq-panel]");
        item.classList.toggle("is-open", open);
        trigger?.setAttribute("aria-expanded", String(open));
        if (panel) panel.hidden = !open;
      };

      items.forEach((item) => {
        item.querySelector("[data-faq-trigger]")?.addEventListener("click", () => {
          const shouldOpen = !item.classList.contains("is-open");
          items.forEach((current) => setItem(current, current === item && shouldOpen));
        });
      });
    });
  }

  initMegaMenuPreviews() {
    const previewCache = new Map();

    document.querySelectorAll(".catalog-category-menu").forEach((menu) => {
      const inner = menu.querySelector(".catalog-category-menu__inner");
      const links = [
        ...menu.querySelectorAll(".catalog-category-menu__columns a"),
      ];
      if (!inner || !links.length) return;

      const preview = document.createElement("aside");
      preview.className = "catalog-category-menu__preview";
      preview.setAttribute("aria-live", "polite");
      preview.innerHTML = `
        <div class="catalog-category-menu__preview-media">
          <span>Preview</span>
          <img alt="" loading="lazy">
        </div>
        <p class="eyebrow">Selected item</p>
        <h3></h3>
        <p class="catalog-category-menu__preview-text">Hover over a product to preview it.</p>
      `;
      inner.append(preview);

      const image = preview.querySelector("img");
      const title = preview.querySelector("h3");
      const text = preview.querySelector(
        ".catalog-category-menu__preview-text",
      );

      const fetchPreview = async (link) => {
        const url = new URL(link.href, window.location.origin).href;
        if (!url.startsWith(window.location.origin)) return null;
        if (!previewCache.has(url)) {
          previewCache.set(
            url,
            fetch(url, { credentials: "same-origin" })
              .then((response) => {
                if (!response.ok) throw new Error("Preview unavailable");
                return response.text();
              })
              .then((html) => {
                const documentPreview = new DOMParser().parseFromString(
                  html,
                  "text/html",
                );
                return {
                  image:
                    documentPreview.querySelector('meta[property="og:image"]')
                      ?.content ||
                    documentPreview.querySelector('meta[name="twitter:image"]')
                      ?.content ||
                    "",
                  description:
                    documentPreview.querySelector(
                      'meta[property="og:description"]',
                    )?.content ||
                    documentPreview.querySelector('meta[name="description"]')
                      ?.content ||
                    "",
                };
              })
              .catch(() => ({ image: "", description: "" })),
          );
        }
        return previewCache.get(url);
      };

      const showPreview = async (link) => {
        const requestedUrl = new URL(link.href, window.location.origin).href;
        preview.dataset.requestedUrl = requestedUrl;
        title.textContent = link.textContent.trim();
        text.textContent = "Loading product preview…";
        preview.classList.add("is-loading");

        const result = await fetchPreview(link);
        if (preview.dataset.requestedUrl !== requestedUrl || !result) return;
        if (result.image) {
          image.src = new URL(result.image, requestedUrl).href;
          image.alt = link.textContent.trim();
          preview.classList.add("has-image");
        } else {
          image.removeAttribute("src");
          image.alt = "";
          preview.classList.remove("has-image");
        }
        text.textContent =
          result.description ||
          "Open this item to see all available details and options.";
        preview.classList.remove("is-loading");
      };

      links.forEach((link) => {
        link.addEventListener("mouseenter", () => showPreview(link));
        link.addEventListener("focus", () => showPreview(link));
      });
      menu.closest("[data-nav-item]")?.addEventListener(
        "mouseenter",
        () => {
          if (!title.textContent) showPreview(links[0]);
        },
        { once: true },
      );
    });
  }

  initWhatsAppOrdering() {
    document.querySelectorAll("[data-product-section]").forEach((section) => {
      const buttons = [
        ...section.querySelectorAll("[data-whatsapp-buy]"),
        ...document.querySelectorAll(
          `[data-product-enquiry="${section.dataset.productId}"]`,
        ),
      ];
      if (!buttons.length) return;

      const updateLinks = () => {
        const option = section.querySelector("[data-variant-select]");
        const quantity =
          section.querySelector("[data-quantity] input")?.value || "1";
        const notes = section.querySelector("[data-order-notes]")?.value.trim();

        buttons.forEach((button) => {
          const phone = String(button.dataset.whatsappPhone || "").replace(
            /\D/g,
            "",
          );
          const lines = [
            button.dataset.whatsappPrefix ||
              "Hello Laserco, I would like to order:",
            button.dataset.productTitle || "",
          ];
          if (option?.selectedOptions?.[0]?.textContent.trim()) {
            lines.push(
              `Option: ${option.selectedOptions[0].textContent.trim()}`,
            );
          }
          lines.push(`Quantity: ${quantity}`);
          if (notes) lines.push(`Requirements: ${notes}`);
          if (button.dataset.productUrl) lines.push(button.dataset.productUrl);
          button.href = `https://wa.me/${phone}?text=${encodeURIComponent(lines.filter(Boolean).join("\n"))}`;
        });
      };

      section.addEventListener("input", updateLinks);
      section.addEventListener("change", updateLinks);
      buttons.forEach((button) =>
        button.addEventListener("click", updateLinks),
      );
      updateLinks();
    });
  }

  initProductGallery() {
    document.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
      const thumbnails = [...gallery.querySelectorAll("[data-media-target]")];
      const mediaItems = [...gallery.querySelectorAll("[data-product-media]")];
      thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener("click", () => {
          const target = thumbnail.dataset.mediaTarget;
          thumbnails.forEach((item) =>
            item.classList.toggle("is-active", item === thumbnail),
          );
          mediaItems.forEach((media) =>
            media.classList.toggle("is-active", media.dataset.productMedia === target),
          );
        });
      });
    });
  }

  initQuantity() {
    document.querySelectorAll("[data-quantity]").forEach((quantity) => {
      const input = quantity.querySelector("input");
      quantity
        .querySelector("[data-quantity-minus]")
        ?.addEventListener("click", () => {
          input.value = Math.max(
            Number(input.min || 1),
            Number(input.value || 1) - 1,
          );
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      quantity
        .querySelector("[data-quantity-plus]")
        ?.addEventListener("click", () => {
          input.value = Number(input.value || 1) + 1;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
    });
  }

  initStickyWhatsApp() {
    const sticky = document.querySelector("[data-sticky-whatsapp]");
    const mainButton = document.querySelector("[data-main-whatsapp]");
    if (!sticky || !mainButton) return;
    const observer = new IntersectionObserver(
      ([entry]) => sticky.classList.toggle("is-visible", !entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(mainButton);
  }
}

document.addEventListener("DOMContentLoaded", () => new LasercoTheme());
